import React, { CSSProperties, useState } from 'react';
import styled, { ThemeProps } from 'styled-components';
import { colors } from './colors';
import fonts from './fonts';
import { P1 } from './Texts';
import { Theme } from './types';
import { themeState } from './theme';
import { Icon, IconType } from './Icons';
import { ClipLoader } from 'react-spinners';
import { CornerRadius, Spacing } from './spacing';
import Link from 'next/link';
import { View } from './View';
import { MicroIcon, MicroIconType } from './Icons/MicroIcon';

// Buttons are for clicking and changing state
// Links are for navigating inside the app with routes

// ============================================================================
// BUTTON SIZE CONFIGURATION
// Single source of truth for all button size variants
// ============================================================================

type ButtonSize = 'sm' | 'md' | 'lg';

const ButtonSizes: Record<
  ButtonSize,
  {
    height: number;
    paddingHorizontal: number;
    borderRadius: number;
    fontSize: number;
    lineHeight: string;
  }
> = {
  sm: {
    height: 32,
    paddingHorizontal: Spacing.sm + Spacing.xs, // 12
    borderRadius: CornerRadius.xs, // 6
    fontSize: 15,
    lineHeight: `21px`,
  },
  md: {
    height: 38,
    paddingHorizontal: Spacing.md, // 16
    borderRadius: CornerRadius.sm, // 8
    fontSize: 15,
    lineHeight: `21px`,
  },
  lg: {
    height: 48,
    paddingHorizontal: Spacing.md + Spacing.xs, // 20
    borderRadius: CornerRadius.lg, // 12
    fontSize: 17,
    lineHeight: `23px`,
  },
};

// ============================================================================
// PRIMARY BUTTON
// ============================================================================

const StyledPrimaryButton = styled.button<{ size: ButtonSize; enabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  color: ${colors.white};
  outline: none;
  height: ${({ size }) => ButtonSizes[size].height}px;
  padding: 0 ${({ size }) => ButtonSizes[size].paddingHorizontal}px;
  border-radius: ${({ size }) => ButtonSizes[size].borderRadius}px;
  background-color: ${(props: ThemeProps<Theme> & { enabled: boolean }) => {
    if (props.enabled) {
      return colors.primary;
    }
    return props.theme.name === 'dark' ? colors.grey : colors.greyMedium;
  }};
`;

interface PrimaryButtonProps {
  title: string;
  type?: 'submit';
  enabled?: boolean;
  loading?: boolean;
  destructive?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  size?: ButtonSize;
  iconType?: MicroIconType;
}

export const PrimaryButton = ({
  title,
  type,
  enabled = true,
  loading = false,
  destructive = false,
  size = 'md',
  onClick,
  style,
  iconType,
}: PrimaryButtonProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  return (
    <StyledPrimaryButton
      size={size}
      type={type}
      enabled={enabled}
      style={{
        ...(enabled
          ? {
              cursor: 'pointer',
              pointerEvents: 'auto',
            }
          : {
              cursor: 'default',
              pointerEvents: 'none',
            }),
        ...(loading ? { pointerEvents: 'none' } : {}),
        ...(isHovering && enabled && { backgroundColor: colors.primary600 }),
        ...(isClicking && enabled && { backgroundColor: colors.primary600 }),
        ...style,
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseDown={() => setIsClicking(true)}
      onMouseUp={() => setIsClicking(false)}
      onMouseOut={() => setIsClicking(false)}
    >
      {loading ? (
        <ClipLoader size={20} color={colors.white} loading={true} />
      ) : (
        <>
          {iconType && (
            <MicroIcon
              type={iconType}
              style={{ marginInlineEnd: Spacing.sm }}
              tint={colors.white}
            />
          )}
          <P1
            style={{
              color: destructive ? colors.red : colors.white,
              fontWeight: fonts.medium.weight,
              lineHeight: ButtonSizes[size].lineHeight,
              fontSize: ButtonSizes[size].fontSize,
            }}
          >
            {title}
          </P1>
        </>
      )}
    </StyledPrimaryButton>
  );
};

// ============================================================================
// SECONDARY BUTTON
// ============================================================================

const StyledSecondaryButtonP1 = styled(P1)<{ enabled: boolean; destructive: boolean }>`
  color: ${(props: ThemeProps<Theme> & { enabled: boolean; destructive: boolean }) => {
    if (props.enabled) {
      return props.destructive
        ? colors.red
        : props.theme.name === 'light'
          ? colors.blackLight
          : colors.white;
    }

    return props.theme.name === 'light' ? colors.greyMedium : colors.grey;
  }};
  font-weight: ${fonts.medium.weight};
`;

const StyledSecondaryButton = styled.button<{ size: ButtonSize; enabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  pointer-events: auto;
  outline: none;
  height: ${({ size }) => ButtonSizes[size].height}px;
  padding: 0 ${({ size }) => ButtonSizes[size].paddingHorizontal}px;
  border-radius: ${({ size }) => ButtonSizes[size].borderRadius}px;
  background-color: ${(props: ThemeProps<Theme> & { enabled: boolean }) => {
    if (props.enabled) {
      return props.theme.name === 'light' ? colors.greyUltraLight : colors.greyDark;
    }
    return props.theme.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark;
  }};
`;

interface SecondaryButtonProps {
  title: string;
  size?: ButtonSize;
  enabled?: boolean;
  loading?: boolean;
  destructive?: boolean;
  iconType?: IconType;
  iconStyle?: CSSProperties;
  microIconType?: MicroIconType;
  onClick?: () => void;
  style?: CSSProperties;
}

export const SecondaryButton = ({
  title,
  loading = false,
  destructive = false,
  onClick,
  style,
  iconType,
  iconStyle,
  enabled = true,
  size = 'md',
  microIconType,
}: SecondaryButtonProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  return (
    <StyledSecondaryButton
      size={size}
      enabled={enabled}
      style={{
        ...(isHovering &&
          enabled && {
            backgroundColor:
              themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark,
          }),
        ...(isClicking && {
          backgroundColor:
            themeState.current.name === 'light' ? colors.greyUltraLight : colors.blackLight,
        }),
        ...(loading ? { pointerEvents: 'none' } : {}),
        ...style,
        cursor: enabled ? 'pointer' : 'default',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseDown={() => setIsClicking(true)}
      onMouseUp={() => setIsClicking(false)}
      onMouseOut={() => setIsClicking(false)}
    >
      {microIconType ? (
        <MicroIcon
          type={microIconType}
          style={{ marginInlineStart: Spacing.sm, marginInlineEnd: Spacing.sm, ...iconStyle }}
          tint={themeState.current.primary}
        />
      ) : iconType ? (
        <Icon
          type={iconType}
          style={{ marginInlineStart: Spacing.sm, marginInlineEnd: Spacing.sm, ...iconStyle }}
          tint={themeState.current.primary}
        />
      ) : null}

      <StyledSecondaryButtonP1
        destructive={destructive}
        style={{ fontSize: ButtonSizes[size].fontSize, lineHeight: ButtonSizes[size].lineHeight }}
        enabled={enabled}
      >
        {loading ? 'Loading...' : title}
      </StyledSecondaryButtonP1>
    </StyledSecondaryButton>
  );
};

// ============================================================================
// TERTIARY BUTTON
// ============================================================================

export const TertiaryButtonLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${colors.primary};
  text-decoration: none;
  font-family: ${fonts.medium.family};
  font-weight: ${fonts.medium.weight};
  font-size: 15px;
  line-height: 21px;
  cursor: pointer;
`;

// ============================================================================
// Link Components
// ============================================================================

export const PrimaryButtonLink = (props: PrimaryButtonProps & { to: string }) => {
  return (
    <Link href={props.to} style={{ textDecoration: 'none' }}>
      <View>
        <PrimaryButton {...props} />
      </View>
    </Link>
  );
};

export const TertiaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${colors.primary};
  text-decoration: none;
  font-family: ${fonts.medium.family};
  font-weight: ${fonts.medium.weight};
  font-size: 15px;
  line-height: 21px;
  cursor: pointer;
  outline: none;
`;
